import React, { createContext, useContext, useEffect, useState } from 'react';
import { api_url } from '../constants/api';

interface Branding {
    appName: string;
    version: string;
    normalLogo: string | null;
    darkLogo: string | null;
    isRemoteBranding: boolean;
}

const fallbackBranding: Branding = {
    appName: 'PUSHVAULT',
    version: '1.0.5',
    normalLogo: null,
    darkLogo: null,
    isRemoteBranding: false
};

const BrandingContext = createContext<Branding>(fallbackBranding);

const fetchLogo = async (path: string): Promise<Blob> => {
    const response = await fetch(`${api_url}${path}`, { cache: 'no-store' });
    if (!response.ok) {
        throw new Error(`Failed to fetch ${path}`);
    }

    const blob = await response.blob();
    if (!blob.type.startsWith('image/') || blob.size === 0) {
        throw new Error(`Invalid image returned by ${path}`);
    }

    return blob;
};

const validateLogo = async (blob: Blob, label: string): Promise<string> => {
    const objectUrl = URL.createObjectURL(blob);
    try {
        await new Promise<void>((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = () => reject(new Error(`Invalid image returned by ${label}`));
            image.src = objectUrl;
        });
    } catch (error) {
        URL.revokeObjectURL(objectUrl);
        throw error;
    }

    return objectUrl;
};

export const BrandingProvider = ({ children }: { children: React.ReactNode }) => {
    const [branding, setBranding] = useState<Branding>(fallbackBranding);

    useEffect(() => {
        let active = true;
        let loadedLogoUrls: string[] = [];

        const loadBranding = async () => {
            try {
                const [appInfoResponse, normalLogoBlob, darkLogoBlob] = await Promise.all([
                    fetch(`${api_url}/getAppInfo`, { cache: 'no-store' }),
                    fetchLogo('/getNormalLogo'),
                    fetchLogo('/getDarkLogo')
                ]);

                if (!appInfoResponse.ok) {
                    throw new Error('Failed to fetch app information');
                }

                const appInfo = await appInfoResponse.json();
                if (
                    typeof appInfo?.name !== 'string' ||
                    !appInfo.name.trim() ||
                    typeof appInfo?.version !== 'string' ||
                    !appInfo.version.trim()
                ) {
                    throw new Error('Invalid app information');
                }

                const [normalLogo, darkLogo] = await Promise.all([
                    validateLogo(normalLogoBlob, '/getNormalLogo'),
                    validateLogo(darkLogoBlob, '/getDarkLogo')
                ]);
                loadedLogoUrls = [normalLogo, darkLogo];

                if (!active) return;

                const nextBranding: Branding = {
                    appName: appInfo.name.trim(),
                    version: appInfo.version.trim(),
                    normalLogo,
                    darkLogo,
                    isRemoteBranding: true
                };

                setBranding(nextBranding);
                document.title = nextBranding.appName;
                document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"], link[rel="apple-touch-icon"]')
                    .forEach(link => {
                        link.href = normalLogo;
                    });
            } catch (error) {
                loadedLogoUrls.forEach(url => URL.revokeObjectURL(url));
                loadedLogoUrls = [];
                console.warn('Using built-in PushVault branding:', error);
            }
        };

        void loadBranding();

        return () => {
            active = false;
            loadedLogoUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    return (
        <BrandingContext.Provider value={branding}>
            {children}
        </BrandingContext.Provider>
    );
};

export const useBranding = () => useContext(BrandingContext);

export const BrandLogo = ({
    variant = 'dark',
    className = ''
}: {
    variant?: 'normal' | 'dark';
    className?: string;
}) => {
    const branding = useBranding();
    const logo = variant === 'normal' ? branding.normalLogo : branding.darkLogo;

    if (logo) {
        return (
            <img
                src={logo}
                alt={`${branding.appName} logo`}
                className={`object-contain ${className}`}
            />
        );
    }

    return (
        <div
            role="img"
            aria-label="PUSHVAULT logo"
            className={`flex items-center font-black uppercase italic tracking-tighter whitespace-nowrap ${className}`}
        >
            <span className="text-white">PUSH</span>
            <span className="text-red-600">VAULT</span>
        </div>
    );
};
